import React from 'react';
import {
    Chip,
    Stack,
    Tooltip,
    Typography,
    useTokens,
    styled,
} from '@barracuda-internal/bds-core';
import { formatTimestamp } from '../../../../common/utils/formatTimestamp';
import { CATEGORY_LABEL } from '../findings/categoryToScanner';
import { PROVIDER_LABEL } from '../findings/providerIcon';
import ProviderBrand from '../findings/ProviderBrand';

// ---------------------------------------------------------------------------
// Local subcomponents
// ---------------------------------------------------------------------------

/**
 * Section header — small uppercase label (3+ CSS properties → styled).
 */
const SectionLabel = styled(Typography, {
    shouldForwardProp: (p) => !['subtleColor'].includes(p),
})(({ subtleColor }) => ({
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: subtleColor,
}));

/**
 * One key/value row inside a section.
 */
function FieldRow({ label, value }) {
    const tokens = useTokens();
    if (value === null || value === undefined || value === '') return null;
    return (
        <Stack
            direction="row"
            sx={{
                justifyContent: 'space-between',
                padding: `${tokens.aliasesSpaceComponentDense} 0`,
            }}
        >
            <Typography variant="captionSubtle">{label}</Typography>
            <Typography variant="caption">{value}</Typography>
        </Stack>
    );
}

/**
 * Section wrapper — title row + a list of [label, value] pairs.
 * Rows with null/undefined/empty values are skipped automatically.
 */
function Section({ title, rows }) {
    const tokens = useTokens();
    const visibleRows = rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
    if (visibleRows.length === 0) return null;
    return (
        <Stack
            component="section"
            sx={{ gap: tokens.aliasesSpaceComponentDense, marginBottom: tokens.aliasesSpaceContainerDefault }}
        >
            <SectionLabel variant="micro" subtleColor={tokens.aliasesColorTextStaticSubtle}>
                {title}
            </SectionLabel>
            {visibleRows.map(([k, v]) => (
                <FieldRow key={k} label={k} value={v} />
            ))}
        </Stack>
    );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function OverviewTab({ scan }) {
    const tokens = useTokens();

    const started = scan.started_at ? formatTimestamp(scan.started_at) : null;
    const completed = scan.completed_at ? formatTimestamp(scan.completed_at) : null;
    const sizeKb =
        scan.content_size_bytes != null
            ? `${(scan.content_size_bytes / 1024).toFixed(1)} KB`
            : null;
    const durationLabel =
        scan.latency_ms != null
            ? `${scan.latency_ms.toLocaleString()} ms`
            : null;

    return (
        <Stack sx={{ gap: tokens.aliasesSpaceComponentDense }}>
            {/* Provider chip — only when provider is set */}
            {scan.provider && (
                <Stack
                    direction="row"
                    sx={{ gap: tokens.aliasesSpaceComponentDense, alignItems: 'center', marginBottom: tokens.aliasesSpaceContainerDefault }}
                >
                    <Tooltip title={scan.model || 'model unknown'}>
                        <Chip
                            bdsType="staticNeutral"
                            bdsSize="medium"
                            icon={<ProviderBrand slug={scan.provider} size={16} />}
                            label={PROVIDER_LABEL[scan.provider] || scan.provider}
                        />
                    </Tooltip>
                    {scan.model && (
                        <Typography variant="captionSubtle">{scan.model}</Typography>
                    )}
                </Stack>
            )}

            {/* Findings summary */}
            <Stack
                component="section"
                sx={{ gap: tokens.aliasesSpaceComponentDense, marginBottom: tokens.aliasesSpaceContainerDefault }}
            >
                <SectionLabel variant="micro" subtleColor={tokens.aliasesColorTextStaticSubtle}>
                    Findings summary
                </SectionLabel>
                {(scan.finding_categories || []).length > 0 ? (
                    <Stack
                        direction="row"
                        alignItems="flex-start"
                        sx={{ gap: tokens.aliasesSpaceComponentDense, flexWrap: 'wrap' }}
                    >
                        {scan.finding_categories.map((c) => (
                            <Chip
                                key={c}
                                bdsType="staticNeutral"
                                label={CATEGORY_LABEL[c] || c}
                            />
                        ))}
                    </Stack>
                ) : (
                    <Typography variant="captionSubtle">None</Typography>
                )}
            </Stack>

            {/* Request */}
            <Section
                title="Request"
                rows={[
                    ['API Key',   scan.account_name],
                    ['Tenant ID', scan.tenant_id],
                    ['User ID',   scan.user_id],
                    ['Region',    scan.region],
                    ['Mode',      scan.processing_mode || scan.response_mode],
                ]}
            />

            {/* Content */}
            <Section
                title="Content"
                rows={[
                    ['Type', scan.content_type],
                    ['Size', sizeKb],
                ]}
            />

            {/* Timing */}
            <Section
                title="Timing"
                rows={[
                    ['Started',   started ? `${started.time} (${started.tzLabel})` : null],
                    ['Completed', completed ? `${completed.time} (${completed.tzLabel})` : null],
                    ['Duration',  durationLabel],
                ]}
            />

            {/* Prompt section — always shown; content or "not stored" note */}
            <Stack
                component="section"
                sx={{ gap: tokens.aliasesSpaceComponentDense, marginBottom: tokens.aliasesSpaceContainerDefault }}
            >
                <SectionLabel variant="micro" subtleColor={tokens.aliasesColorTextStaticSubtle}>
                    Prompt
                </SectionLabel>
                {scan.prompt_stored ? (
                    <Typography
                        variant="body1"
                        component="pre"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                        {scan.prompt_stored}
                    </Typography>
                ) : (
                    <Typography variant="captionSubtle">
                        Prompt not stored — enable in Support settings
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}
